import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.turevisionsegura.com';

const partners = [
  { name: 'VELKO', color: 'text-amber-400' },
  { name: 'CARD PLUS', color: 'text-amber-300' },
  { name: 'GESTIÓN AUTOMOTRIZ AW2020', color: 'text-amber-500' },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// Botón CTA principal con efecto glow cobre/naranja
function CtaButton({ children, className = '', onClick, as: Tag = 'button', to, href, ...props }: {
  children: React.ReactNode; className?: string; onClick?: () => void;
  as?: 'button' | typeof Link | 'a'; to?: string; href?: string; [key: string]: unknown;
}) {
  const base = `inline-flex items-center justify-center gap-2 font-black uppercase tracking-widest whitespace-nowrap cursor-pointer transition-all duration-300
    bg-gradient-to-r from-[#C87941] via-[#D4883A] to-[#E8A020]
    hover:from-[#E8A020] hover:via-[#D4883A] hover:to-[#C87941]
    text-white shadow-lg
    hover:shadow-[0_0_28px_6px_rgba(220,100,20,0.55)]
    active:scale-95 ${className}`;
  if (Tag === Link && to) return <Link to={to} className={base} {...props}>{children}</Link>;
  if (Tag === 'a' && href) return <a href={href} className={base} {...props}>{children}</a>;
  return <button onClick={onClick} className={base} {...props}>{children}</button>;
}

export default function Home() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.title = 'REVISION SEGURA | Plataforma de Gestión de Beneficios · Kit VELKO · Colombia';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'REVISION SEGURA es la Plataforma de Gestión de Beneficios de la Alianza Técnica para conductores responsables en Colombia. Kit VELKO premium, 8 asistencias Card Plus y revisión vehicular al costo legal vigente.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/`);
    const schemaId = 'schema-home';
    let el = document.getElementById(schemaId);
    if (!el) { el = document.createElement('script'); el.id = schemaId; (el as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(el); }
    el.textContent = JSON.stringify([
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'REVISION SEGURA', url: SITE_URL },
      { '@context': 'https://schema.org', '@type': 'WebSite', name: 'REVISION SEGURA', url: SITE_URL },
    ]);
    return () => { const s = document.getElementById(schemaId); if (s) s.remove(); };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openAgent = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  return (
    <div
      className="min-h-screen font-sans overflow-x-hidden"
      style={{ background: 'radial-gradient(ellipse at 50% 30%, #1a3a6e 0%, #152d55 55%, #0f2040 100%)' }}
    >
      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'backdrop-blur-xl border-b border-[#C87941]/20 shadow-[0_2px_30px_rgba(200,121,65,0.08)]' : 'bg-transparent'}`}
        style={scrolled ? { background: 'rgba(13,27,42,0.92)' } : {}}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-end h-16">
          <div className="hidden md:flex items-center gap-8">
            {([['/', 'INICIO'], ['/servicios', 'RECOMPENSAS'], ['/contacto', 'CONTACTO']] as [string, string][]).map(([to, label]) => (
              <Link key={to} to={to}
                className={`text-sm font-bold tracking-wider transition-colors whitespace-nowrap ${to === '/' ? 'text-[#C87941]' : 'text-white/70 hover:text-white'}`}>
                {label}
              </Link>
            ))}
          </div>
          <button className="md:hidden text-white/70 w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/95 border-t border-white/5 px-6 py-4 flex flex-col gap-3">
            {([['/', 'INICIO'], ['/servicios', 'RECOMPENSAS'], ['/contacto', 'CONTACTO']] as [string, string][]).map(([to, label]) => (
              <Link key={to} to={to} className="text-sm text-white/70 hover:text-white font-bold tracking-wider" onClick={() => setIsMobileMenuOpen(false)}>{label}</Link>
            ))}
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/718ca3742f948702e9ce849eac1fbb87.jpg"
            alt="REVISION SEGURA Hero"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.75) saturate(1.0)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f2040]/50 via-[#152d55]/30 to-[#0f2040]/80"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2040]/40 via-transparent to-[#0f2040]/40"></div>
        </div>

        {/* Partículas doradas */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(18)].map((_, i) => (
            <div key={i} className="absolute w-1 h-1 rounded-full animate-pulse"
              style={{
                background: i % 2 === 0 ? '#C87941' : '#20C0D0',
                left: `${5 + i * 5.2}%`, top: `${10 + (i % 5) * 18}%`,
                animationDelay: `${i * 0.4}s`, opacity: 0.12 + (i % 4) * 0.08
              }} />
          ))}
        </div>

        {/* Glow orbs */}
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(200,121,65,0.06)' }}></div>
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-3xl animate-pulse"
          style={{ background: 'rgba(32,192,208,0.05)', animationDelay: '1.5s' }}></div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center gap-12">
          {/* ── Columna izquierda: texto + CTAs ── */}
          <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-[#C87941]/30 rounded-full px-5 py-2 mb-10 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C87941' }}></span>
              <span className="text-xs text-white/80 font-bold tracking-widest uppercase">PROGRAMA EMPRESARIAL PRIVADO · BOGOTÁ COLOMBIA</span>
            </div>

            {/* Título */}
            <h1 className="text-5xl md:text-7xl font-black text-white leading-none mb-4 tracking-tight">
              LA ÚNICA{' '}
              <span
                className="relative inline-block"
                style={{
                  color: '#FF6B1A',
                  textShadow: '0 0 20px rgba(255,107,26,0.9), 0 0 60px rgba(255,107,26,0.5), 0 0 100px rgba(255,107,26,0.3)',
                }}
              >
                REVISIÓN
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl font-black mb-6" style={{ color: '#C87941' }}>
              que se Paga Sola: Tu RTM con Costo Cero Percibido
            </p>

            {/* Descripción */}
            <p className="text-base text-white/70 max-w-xl mb-10 leading-relaxed">
              En nuestra <strong className="text-white">Alianza de Beneficios</strong>, cumplir con la ley genera recompensas reales. Paga tu tarifa legal de <strong style={{ color: '#C87941' }}>$370.000</strong> y activa de inmediato un paquete de beneficios valorado en <strong className="text-white">más de $300.000</strong>. Respaldado por{' '}
              <span className="font-black" style={{ color: '#C87941' }}>VELKO</span>,{' '}
              <span className="font-black" style={{ color: '#D4883A' }}>CARD PLUS</span>,{' '}
              <span className="font-black" style={{ color: '#E8A020' }}>GESTIÓN AUTOMOTRIZ AW2020</span> y los{' '}
              <span className="font-black text-white/80">Talleres Aliados registrados</span>.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <CtaButton as={Link} to="/contacto" className="px-10 py-4 rounded-sm text-sm">
                <i className="ri-trophy-line text-lg"></i>
                ACTIVAR INCENTIVO DE REGISTRO
              </CtaButton>
            </div>
          </div>

          {/* ── Columna derecha: imagen principal ── */}
          <div className="flex-1 w-full flex flex-col gap-4">
            <div className="w-full overflow-hidden relative"
              style={{
                borderRadius: '4px',
                border: '1px solid rgba(200,121,65,0.35)',
                boxShadow: '0 0 40px rgba(200,121,65,0.18)',
                aspectRatio: '16/9',
              }}>
              {/* Reemplazar este div con <img src="TU_IMAGEN" ... /> cuando tengas la imagen */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                style={{ background: 'linear-gradient(135deg, rgba(15,32,64,0.97) 0%, rgba(26,58,110,0.92) 100%)' }}>
                <div className="w-16 h-16 flex items-center justify-center"
                  style={{ background: 'rgba(200,121,65,0.12)', border: '1px solid rgba(200,121,65,0.3)', borderRadius: '4px' }}>
                  <i className="ri-image-add-line text-3xl" style={{ color: 'rgba(200,121,65,0.55)' }}></i>
                </div>
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: 'rgba(200,121,65,0.45)' }}>Imagen próximamente</p>
              </div>
            </div>

            {/* ── Cajón debajo del video ── */}
            <div className="relative overflow-hidden flex flex-col sm:flex-row items-stretch"
              style={{
                borderRadius: '4px',
                border: '1px solid rgba(200,121,65,0.55)',
                background: 'linear-gradient(135deg, rgba(15,32,64,0.98) 0%, rgba(26,58,110,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 28px rgba(200,121,65,0.22), inset 0 0 40px rgba(200,121,65,0.06)',
              }}>

              {/* Glow de fondo */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(232,160,32,0.10) 0%, transparent 70%)' }}></div>

              {/* ── Placeholder imagen izquierda ── */}
              <div className="relative flex-shrink-0 w-full sm:w-44 h-36 sm:h-auto overflow-hidden flex flex-col items-center justify-center gap-2"
                style={{
                  borderRadius: '4px 0 0 4px',
                  background: 'linear-gradient(135deg, rgba(15,32,64,0.97) 0%, rgba(26,58,110,0.92) 100%)',
                  border: '1px solid rgba(200,121,65,0.2)',
                  minHeight: '120px',
                }}>
                <div className="w-10 h-10 flex items-center justify-center"
                  style={{ background: 'rgba(200,121,65,0.12)', border: '1px solid rgba(200,121,65,0.3)', borderRadius: '4px' }}>
                  <i className="ri-image-add-line text-xl" style={{ color: 'rgba(200,121,65,0.5)' }}></i>
                </div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-center px-2" style={{ color: 'rgba(200,121,65,0.4)' }}>Imagen<br/>próximamente</p>
                {/* Badge sobre el placeholder */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1"
                  style={{
                    background: 'rgba(200,121,65,0.85)',
                    borderRadius: '3px',
                    backdropFilter: 'blur(6px)',
                  }}>
                  <i className="ri-trophy-line text-white text-xs"></i>
                  <span className="text-white text-xs font-black tracking-wider">BENEFICIOS</span>
                </div>
              </div>

              {/* ── Contenido derecho ── */}
              <div className="relative z-10 flex flex-col justify-center gap-3 px-5 py-4 flex-1">
                {/* Título */}
                <p className="font-black text-base md:text-lg leading-tight tracking-wide"
                  style={{ color: '#F5C060', textShadow: '0 0 18px rgba(232,160,32,0.45)' }}>
                  Regístrate, haz tu RTM y activa tu paquete de beneficios exclusivos
                </p>

                {/* Divisor */}
                <div className="w-12 h-px" style={{ background: 'rgba(200,121,65,0.5)' }}></div>

                {/* 3 pasos */}
                <div className="flex flex-col gap-2">
                  {[
                    {
                      num: '1',
                      icon: 'ri-qr-code-line',
                      label: 'Regístrate',
                      desc: 'Escanea el QR del taller y registra tus datos con el código aliado. Activas tu participación por un Reconocimiento Especial basado en los resultados de la Lotería de Bogotá de cada jueves.',
                    },
                    {
                      num: '2',
                      icon: 'ri-car-line',
                      label: 'Realiza tu RTM ($370.000)',
                      desc: 'Al completar tu Revisión Tecnomecánica con el Taller Aliado, recibes automáticamente tu Kit Profesional VELKO y tu Tarjeta Card Plus con 8 asistencias nacionales activas.',
                    },
                    {
                      num: '3',
                      icon: 'ri-money-dollar-circle-line',
                      label: 'Entrega de Beneficios',
                      desc: 'Kit VELKO y Card Plus se entregan ese mismo día. El Bono de Reembolso por Fidelidad para llantas se coordina directamente con el Taller Aliado y el operador. El monto depende del rin, diseño y valor de las llantas seleccionadas.',
                    },
                  ].map((step) => (
                    <div key={step.num} className="flex items-start gap-2.5">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5 font-black text-xs"
                        style={{ background: step.num === '3' ? 'rgba(200,121,65,0.35)' : 'linear-gradient(135deg, #C87941, #E8A020)', borderRadius: '3px', color: '#fff', border: step.num === '3' ? '1px solid rgba(200,121,65,0.6)' : 'none' }}>
                        {step.num}
                      </div>
                      <div>
                        <p className="text-white font-black text-xs leading-none mb-0.5">
                          {step.label}
                          {step.num === '3' && (
                            <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(200,121,65,0.2)', color: '#FF6B1A', border: '1px solid rgba(200,121,65,0.4)' }}>CONDICIONAL</span>
                          )}
                        </p>
                        <p className="text-white/60 text-[11px] leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mecánica del incentivo */}
                <div className="flex items-start gap-2 mt-1"
                  style={{ borderTop: '1px solid rgba(200,121,65,0.2)', paddingTop: '8px' }}>
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5"
                    style={{ background: 'rgba(255,107,26,0.2)', border: '1px solid rgba(255,107,26,0.4)', borderRadius: '3px' }}>
                    <i className="ri-hashtag text-[10px]" style={{ color: '#FF6B1A' }}></i>
                  </div>
                  <p className="text-white/55 text-[11px] leading-relaxed">
                    <strong className="text-white/80">Reconocimiento Especial:</strong> Los <strong className="text-white">3 últimos números de tu placa</strong>, en estricto orden, se comparan con los 3 últimos dígitos del <strong style={{ color: '#E8A020' }}>premio mayor de la Lotería de Bogotá</strong> publicado cada jueves. Si coinciden, el Reconocimiento Especial se activa — hasta el 100% del valor de tu RTM. Aplica para <strong className="text-white/80">vehículos livianos particulares y públicos</strong>.
                  </p>
                </div>

                {/* Destellos decorativos */}
                <div className="absolute right-3 top-3 w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: '#E8A020', opacity: 0.7 }}></div>
                <div className="absolute right-6 bottom-3 w-1 h-1 rounded-full animate-pulse"
                  style={{ background: '#E8A020', opacity: 0.5, animationDelay: '0.7s' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
          <i className="ri-arrow-down-s-line text-white/20 text-2xl"></i>
        </div>
      </section>

      {/* ── TU REVISIÓN SEGURA ── */}
      <section className="py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #0f2040 0%, #152d55 50%, #1a3a6e 100%)' }}>
        {/* Fondo decorativo */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(200,121,65,0.12) 0%, transparent 70%)' }}></div>
          <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(32,192,208,0.08) 0%, transparent 70%)' }}></div>
          {/* Líneas decorativas */}
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.5), transparent)' }}></div>
          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.3), transparent)' }}></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          {/* Encabezado impactante */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 rounded-full"
              style={{ background: 'rgba(200,121,65,0.12)', border: '1px solid rgba(200,121,65,0.35)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C87941' }}></span>
              <span className="text-xs text-white/70 font-bold tracking-widest uppercase">Alianza de Beneficios · Programa Empresarial Privado</span>
            </div>

            <h2 className="text-5xl md:text-7xl font-black text-white leading-none mb-5 tracking-tight">
              EL PAQUETE{' '}
              <span
                style={{
                  color: '#FF6B1A',
                  textShadow: '0 0 30px rgba(255,107,26,0.8), 0 0 80px rgba(255,107,26,0.4)',
                }}
              >
                DE BENEFICIOS
              </span>
              <br />
              <span
                style={{
                  color: '#E8A020',
                  textShadow: '0 0 30px rgba(232,160,32,0.7), 0 0 60px rgba(232,160,32,0.3)',
                }}
              >
                QUE SE PAGA SOLO
              </span>
            </h2>

            <p className="text-xl md:text-2xl font-black tracking-widest uppercase mb-2" style={{ color: '#D4883A' }}>
              Tarifa legal de $370.000 · Beneficios valorados en más de $300.000
            </p>
            <p className="text-white/50 text-base max-w-xl mx-auto">
              La Alianza de Beneficios que entrega más valor por el mismo precio de ley al realizar tu proceso con nuestros aliados.
            </p>
          </div>

          {/* Tarjeta principal rediseñada */}
          <div className="relative overflow-hidden mb-10"
            style={{
              borderRadius: '6px',
              border: '1px solid rgba(200,121,65,0.4)',
              background: 'rgba(15,32,64,0.97)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 0 60px rgba(200,121,65,0.12), inset 0 0 80px rgba(200,121,65,0.04)',
            }}>
            {/* Barra superior dorada */}
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #C87941, #E8A020, #FF6B1A, #E8A020, #C87941)' }}></div>

            <div className="p-10 md:p-14">
              {/* Bloque 1 */}
              <div className="flex gap-6 items-start mb-10">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,107,26,0.3), rgba(232,160,32,0.2))',
                    border: '1px solid rgba(255,107,26,0.6)',
                    borderRadius: '6px',
                    boxShadow: '0 0 24px rgba(255,107,26,0.3)',
                  }}>
                  <i className="ri-percent-fill text-2xl" style={{ color: '#FF6B1A' }}></i>
                </div>
                <div>
                  <h3 className="text-white font-black text-xl md:text-2xl mb-2 leading-tight">
                    Reconocimiento Especial —{' '}
                    <span className="text-4xl md:text-5xl font-black"
                      style={{
                        color: '#FF6B1A',
                        textShadow: '0 0 20px rgba(255,107,26,0.7)',
                      }}>
                      Lotería de Bogotá
                    </span>
                  </h3>
                  <p className="text-white/70 text-base leading-relaxed">
                    Al <strong className="text-white">registrarte con el código de tu Taller Aliado</strong>, activas tu participación por un Reconocimiento Especial. Si los 3 últimos dígitos de tu placa coinciden con el premio mayor publicado cada jueves, <strong style={{ color: '#E8A020' }}>recuperas hasta el 100% del valor de tu RTM</strong>. Un premio a tu responsabilidad vial — reconocimiento comercial de la Alianza de Beneficios, no un juego de azar.
                  </p>
                </div>
              </div>

              <div className="h-px w-full mb-10" style={{ background: 'linear-gradient(90deg, rgba(32,192,208,0.3), transparent)' }}></div>

              {/* Bloque 2 — Kit VELKO + Card Plus */}
              <div className="flex gap-6 items-start mb-10">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(32,192,208,0.2), rgba(32,192,208,0.1))',
                    border: '1px solid rgba(32,192,208,0.5)',
                    borderRadius: '6px',
                    boxShadow: '0 0 20px rgba(32,192,208,0.2)',
                  }}>
                  <i className="ri-calendar-check-fill text-2xl" style={{ color: '#20C0D0' }}></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-white font-black text-xl md:text-2xl leading-tight">
                      Kit de Prevención VELKO + Card Plus (8 Asistencias)
                    </h3>
                    <span className="text-[10px] font-black px-2.5 py-1 uppercase tracking-widest"
                      style={{ background: 'rgba(32,192,208,0.15)', border: '1px solid rgba(32,192,208,0.4)', borderRadius: '3px', color: '#20C0D0' }}>
                      AL HACER TU RTM
                    </span>
                  </div>
                  <p className="text-white/70 text-base leading-relaxed">
                    Al <strong className="text-white">completar tu Revisión Tecnomecánica con el Taller Aliado</strong>, recibes sin costo adicional tu <strong style={{ color: '#C87941' }}>Kit Profesional VELKO</strong> — un beneficio físico de prevención (líquidos y limpieza) para el cuidado de tu vehículo, entregado por la Alianza al finalizar tu revisión. Además, activas tu <strong style={{ color: '#C87941' }}>Tarjeta Card Plus</strong> con <strong className="text-white">8 asistencias nacionales</strong> (grúa, combustible, cerrajería y más). Protección 24/7 que multiplica el valor de tu inversión. Los vehículos de <strong className="text-white">servicio público de pasajeros</strong> reciben adicionalmente <strong style={{ color: '#E8A020' }}>Revisiones Preventivas Gratuitas</strong>.
                  </p>
                </div>
              </div>

              <div className="h-px w-full mb-10" style={{ background: 'linear-gradient(90deg, rgba(200,121,65,0.4), transparent)' }}></div>

              {/* Bloque 3 — Bono de Llantas condicional */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(200,121,65,0.25), rgba(232,160,32,0.15))',
                    border: '1px solid rgba(200,121,65,0.5)',
                    borderRadius: '6px',
                    boxShadow: '0 0 20px rgba(200,121,65,0.2)',
                  }}>
                  <i className="ri-money-dollar-circle-line text-2xl" style={{ color: '#C87941' }}></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-white font-black text-xl md:text-2xl leading-tight">
                      Bono de Reembolso por Fidelidad para Llantas — hasta $100.000
                    </h3>
                    <span className="text-[10px] font-black px-2.5 py-1 uppercase tracking-widest"
                      style={{ background: 'rgba(255,107,26,0.12)', border: '1px solid rgba(255,107,26,0.4)', borderRadius: '3px', color: '#FF6B1A' }}>
                      SOLO SI COMPRAS LLANTAS
                    </span>
                  </div>
                  <p className="text-white/70 text-base leading-relaxed">
                    Bono de hasta <strong className="text-white">$100.000</strong> para la renovación de tus llantas. Aplica para todos los rines y todas las marcas. El <strong style={{ color: '#C87941' }}>Reembolso por Fidelidad</strong> es gestionado por la Alianza tras tu compra coordinada con el Taller Aliado y el operador. Este bono aplica <strong className="text-white">únicamente si adquieres llantas con nuestra alianza en el taller</strong>.
                  </p>
                </div>
              </div>

              <div className="h-px w-full mb-10" style={{ background: 'linear-gradient(90deg, rgba(200,121,65,0.4), transparent)' }}></div>

              {/* Bloque 4 — Registro */}
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(200,121,65,0.25), rgba(232,160,32,0.15))',
                    border: '1px solid rgba(200,121,65,0.5)',
                    borderRadius: '6px',
                    boxShadow: '0 0 20px rgba(200,121,65,0.2)',
                  }}>
                  <i className="ri-user-add-fill text-2xl" style={{ color: '#C87941' }}></i>
                </div>
                <div>
                  <h3 className="text-white font-black text-xl md:text-2xl mb-2 leading-tight">
                    Registro en QR — Paso Previo Obligatorio
                  </h3>
                  <p className="text-white/70 text-base leading-relaxed">
                    <strong className="text-white">Escanea el QR del taller</strong> y registra tus datos para activar tu participación en el Programa Empresarial Privado. La coordinación de marcas y precios de llantas se realiza directamente con el Taller Aliado. Solo a través del canal de contacto asignado por el Taller Aliado u Operador Automotriz registrado.
                  </p>
                </div>
              </div>
            </div>

            {/* Barra inferior */}
            <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.3), transparent)' }}></div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-4">
            <CtaButton as={Link} to="/contacto" className="px-12 py-5 rounded-sm text-base">
              <i className="ri-trophy-line text-xl"></i>
              ACTIVAR MI PAQUETE DE BENEFICIOS
            </CtaButton>
            <p className="text-white/35 text-xs text-center max-w-md leading-relaxed">
              El Reconocimiento Especial se activa al registrarte. El Kit VELKO y la Card Plus son tu recompensa por completar tu revisión. El Bono de Reembolso por Fidelidad aplica si adquieres llantas con la alianza.
            </p>
          </div>
        </div>
      </section>

      {/* ── RESUMEN DE BENEFICIOS ── */}
      <section className="py-16 relative" style={{ background: 'linear-gradient(180deg, #152d55 0%, #0f2040 100%)' }}>
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.4), transparent)' }}></div>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-black uppercase tracking-widest" style={{ color: '#C87941' }}>Tu paquete de beneficios — condiciones claras</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {/* Beneficio 1 — Lotería */}
            <div className="p-6 flex flex-col gap-3"
              style={{ background: 'rgba(15,32,64,0.9)', border: '1px solid rgba(200,121,65,0.35)', borderRadius: '4px', backdropFilter: 'blur(10px)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #C87941, #E8A020)', borderRadius: '4px' }}>
                  <i className="ri-trophy-line text-white text-base"></i>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5"
                  style={{ background: 'rgba(200,121,65,0.15)', border: '1px solid rgba(200,121,65,0.4)', borderRadius: '3px', color: '#C87941' }}>
                  AL REGISTRARTE
                </span>
              </div>
              <p className="text-white font-black text-sm leading-tight">Reconocimiento Especial — Lotería de Bogotá</p>
              <p className="text-white/60 text-xs leading-relaxed">
                Al registrarte con el código del taller, activas tu participación. Si los 3 últimos dígitos de tu placa coinciden con el premio mayor del jueves, recuperas hasta el 100% del valor de tu RTM. Un premio a tu responsabilidad vial.
              </p>
            </div>

            {/* Beneficio 2 — Kit + Card Plus */}
            <div className="p-6 flex flex-col gap-3"
              style={{ background: 'rgba(15,32,64,0.9)', border: '1px solid rgba(255,107,26,0.4)', borderRadius: '4px', backdropFilter: 'blur(10px)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #FF6B1A, #E8A020)', borderRadius: '4px' }}>
                  <i className="ri-gift-line text-white text-base"></i>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5"
                  style={{ background: 'rgba(255,107,26,0.15)', border: '1px solid rgba(255,107,26,0.4)', borderRadius: '3px', color: '#FF6B1A' }}>
                  AL HACER TU RTM
                </span>
              </div>
              <p className="text-white font-black text-sm leading-tight">Kit Profesional VELKO + Card Plus (8 Asistencias)</p>
              <p className="text-white/60 text-xs leading-relaxed">
                El día que realizas tu RTM con el Taller Aliado, recibes tu Kit de Prevención VELKO (líquidos y limpieza) y tu Tarjeta Card Plus con 8 asistencias nacionales activas. Protección 24/7. <strong className="text-white/80">No requiere compra de llantas.</strong>
              </p>
            </div>

            {/* Beneficio 3 — Bono Llantas */}
            <div className="p-6 flex flex-col gap-3"
              style={{ background: 'rgba(15,32,64,0.9)', border: '1px solid rgba(232,160,32,0.3)', borderRadius: '4px', backdropFilter: 'blur(10px)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(232,160,32,0.25)', border: '1px solid rgba(232,160,32,0.5)', borderRadius: '4px' }}>
                  <i className="ri-money-dollar-circle-line text-base" style={{ color: '#E8A020' }}></i>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5"
                  style={{ background: 'rgba(232,160,32,0.12)', border: '1px solid rgba(232,160,32,0.4)', borderRadius: '3px', color: '#E8A020' }}>
                  SOLO SI COMPRAS LLANTAS
                </span>
              </div>
              <p className="text-white font-black text-sm leading-tight">Bono de Reembolso por Fidelidad — hasta $100.000</p>
              <p className="text-white/60 text-xs leading-relaxed">
                Aplica <strong className="text-white">únicamente si adquieres llantas con la alianza</strong> en el taller. El Reembolso por Fidelidad es gestionado por la Alianza. El monto depende del rin, diseño y valor de las llantas. Aplica para todos los rines y marcas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0f2040 0%, #0a1830 100%)' }}>
        {/* Líneas decorativas */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.4), transparent)' }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,121,65,0.2), transparent)' }}></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          {/* Encabezado */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6 px-5 py-2 rounded-full"
              style={{ background: 'rgba(200,121,65,0.1)', border: '1px solid rgba(200,121,65,0.3)' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#C87941' }}></span>
              <span className="text-xs text-white/60 font-bold tracking-widest uppercase">Proceso Simple · 3 Pasos</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-none mb-4 tracking-tight">
              ¿CÓMO{' '}
              <span style={{ color: '#FF6B1A', textShadow: '0 0 30px rgba(255,107,26,0.7)' }}>FUNCIONA?</span>
            </h2>
            <p className="text-white/50 text-base max-w-lg mx-auto">
              Registro en QR → Realización de RTM ($370.000) → Entrega de Beneficios (Kit VELKO, Card Plus y Bono de Llantas si aplica).
            </p>
          </div>

          {/* Pasos */}
          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Línea conectora desktop */}
            <div className="hidden md:block absolute top-16 left-[calc(16.66%+24px)] right-[calc(16.66%+24px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(200,121,65,0.6), rgba(232,160,32,0.8), rgba(200,121,65,0.6))' }}></div>

            {[
              {
                num: '01',
                icon: 'ri-qr-code-line',
                color: '#C87941',
                glow: 'rgba(200,121,65,0.35)',
                title: 'Registro en QR',
                subtitle: 'Activa tu participación',
                desc: 'Escanea el QR del taller aliado y registra tus datos con el código del Programa Empresarial Privado. Al registrarte, activas tu participación por un Reconocimiento Especial basado en los resultados de la Lotería de Bogotá de cada jueves.',
                tag: 'PASO 1 — REGISTRO',
                badge: null,
              },
              {
                num: '02',
                icon: 'ri-car-line',
                color: '#FF6B1A',
                glow: 'rgba(255,107,26,0.35)',
                title: 'Realización de RTM',
                subtitle: 'Kit VELKO + Card Plus activados',
                desc: 'Al completar tu Revisión Tecnomecánica ($370.000) con el Taller Aliado, recibes tu Kit Profesional VELKO y tu Tarjeta Card Plus con 8 asistencias nacionales activas. Servicio público de pasajeros: también incluye Revisiones Preventivas Gratuitas.',
                tag: 'PASO 2 — RTM $370.000',
                badge: null,
              },
              {
                num: '03',
                icon: 'ri-money-dollar-circle-line',
                color: '#E8A020',
                glow: 'rgba(232,160,32,0.35)',
                title: 'Entrega de Beneficios',
                subtitle: 'Kit, Card Plus y Bono Llantas',
                desc: 'Kit VELKO y Card Plus se entregan ese mismo día. El Bono de Reembolso por Fidelidad para llantas se coordina directamente con el Taller Aliado y el operador. El monto depende del rin, diseño y valor de las llantas seleccionadas.',
                tag: 'PASO 3 — BENEFICIOS',
                badge: 'BONO LLANTAS: SOLO SI APLICA',
              },
            ].map((step, i) => (
              <div key={i} className="relative flex flex-col items-center text-center group">
                {/* Número grande de fondo */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[120px] font-black leading-none select-none pointer-events-none"
                  style={{ color: 'rgba(255,255,255,0.02)', zIndex: 0 }}>
                  {step.num}
                </div>

                {/* Ícono */}
                <div className="relative z-10 w-32 h-32 flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105"
                  style={{
                    background: `radial-gradient(circle, ${step.glow} 0%, rgba(15,32,64,0.95) 70%)`,
                    border: `1px solid ${step.color}55`,
                    borderRadius: '4px',
                    boxShadow: `0 0 40px ${step.glow}`,
                  }}>
                  <i className={`${step.icon} text-5xl`} style={{ color: step.color }}></i>
                  {/* Punto numerado */}
                  <div className="absolute -top-3 -right-3 w-7 h-7 flex items-center justify-center font-black text-xs text-white"
                    style={{ background: `linear-gradient(135deg, ${step.color}, #E8A020)`, borderRadius: '50%' }}>
                    {i + 1}
                  </div>
                </div>

                {/* Tag */}
                <span className="inline-flex items-center px-3 py-1 text-[10px] font-black tracking-widest uppercase mb-3"
                  style={{ background: `${step.color}18`, border: `1px solid ${step.color}40`, borderRadius: '3px', color: step.color }}>
                  {step.tag}
                </span>

                {/* Contenido */}
                <div className="relative z-10 p-6 w-full"
                  style={{
                    background: 'rgba(15,32,64,0.85)',
                    border: i === 2 ? '1px solid rgba(232,160,32,0.35)' : '1px solid rgba(200,121,65,0.18)',
                    borderRadius: '4px',
                    backdropFilter: 'blur(10px)',
                  }}>
                  <h3 className="text-white font-black text-lg mb-1 leading-tight">{step.title}</h3>
                  <p className="text-xs font-bold mb-3 uppercase tracking-widest" style={{ color: step.color }}>{step.subtitle}</p>
                  <p className="text-white/55 text-sm leading-relaxed">{step.desc}</p>
                  {step.badge && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest"
                      style={{ background: 'rgba(232,160,32,0.12)', border: '1px solid rgba(232,160,32,0.4)', borderRadius: '3px', color: '#E8A020' }}>
                      <i className="ri-information-line"></i>
                      {step.badge}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA final */}
          <div className="mt-14 text-center">
            <CtaButton as={Link} to="/contacto" className="px-12 py-5 rounded-sm text-base">
              <i className="ri-qr-code-line text-xl"></i>
              ACTIVAR MI PAQUETE DE BENEFICIOS
            </CtaButton>
            <p className="text-white/30 text-xs mt-4 max-w-sm mx-auto leading-relaxed">
              El registro es presencial en el taller aliado. El código del Programa Empresarial Privado es entregado por el canal oficial.
            </p>
          </div>
        </div>
      </section>

      {/* ── AVISOOFICIAL ── */}
      <section className="py-20" style={{ background: '#152d55' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="relative overflow-hidden p-10 text-center"
            style={{
              borderRadius: '4px',
              border: '1px solid rgba(32,192,208,0.35)',
              background: 'rgba(26,58,110,0.95)',
              backdropFilter: 'blur(16px)',
              boxShadow: 'inset 0 0 60px rgba(32,192,208,0.08)',
            }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(200,121,65,0.1) 0%, transparent 70%)' }}></div>
            <div className="relative z-10">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                style={{ background: 'rgba(32,192,208,0.18)', border: '1px solid rgba(32,192,208,0.4)', borderRadius: '4px' }}>
                <i className="ri-shield-check-line text-2xl" style={{ color: '#20C0D0' }}></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Aviso de Comunicación</h2>
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-5"
                style={{ background: 'rgba(255,107,26,0.12)', border: '1px solid rgba(255,107,26,0.35)', borderRadius: '4px' }}>
                <i className="ri-user-location-line text-sm" style={{ color: '#FF6B1A' }}></i>
                <span className="text-xs font-black uppercase tracking-widest" style={{ color: '#FF6B1A' }}>Canal estrictamente presencial</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-3 max-w-xl mx-auto">
                La Alianza de Beneficios opera bajo un <strong className="text-white">canal de atención estrictamente presencial</strong>. No existen canales abiertos de comunicación digital, teléfonos públicos ni redes sociales de contacto directo.
              </p>
              <p className="text-white/55 text-sm leading-relaxed mb-6 max-w-xl mx-auto">
                <strong className="text-white/80">Toda la información del Programa Empresarial Privado</strong> — requisitos, condiciones, beneficios y proceso de registro — se entrega <strong style={{ color: '#20C0D0' }}>exclusivamente a través del canal de contacto</strong> del Taller Aliado u Operador Automotriz registrado en la alianza. La coordinación de marcas y precios de llantas se realiza directamente con el Taller Aliado.
              </p>
              <CtaButton as={Link} to="/contacto" className="px-8 py-3.5 rounded-sm text-sm">
                <i className="ri-qr-code-line"></i>
                ACTIVAR MI PAQUETE DE BENEFICIOS
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA AGENTE ── */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#1a3a6e' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20glowing%20cyan%20teal%20light%20particles%20and%20bokeh%20effects%2C%20futuristic%20technology%20atmosphere%2C%20deep%20dark%20navy%20background%20with%20subtle%20light%20streaks%2C%20premium%20automotive%20brand%20aesthetic%2C%20ultra%20high%20quality%20render&width=1920&height=700&seq=home-agent-cta-v2&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.22, filter: 'saturate(0.8)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #1a3a6e 0%, transparent 40%, #1a3a6e 100%)' }}></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8"
            style={{
              background: 'linear-gradient(135deg, #C87941, #E8A020)',
              borderRadius: '4px',
              boxShadow: '0 0 40px rgba(200,121,65,0.4)',
            }}>
            <i className="ri-robot-2-line text-white text-3xl"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">¿Tienes preguntas sobre tu paquete?</h2>
          <p className="text-white/60 text-lg mb-8 leading-relaxed">
            Nuestro agente inteligente responde en tiempo real, por voz o chat, las 24 horas. Consulta sobre el Programa Empresarial Privado, la Alianza de Beneficios y tus recompensas.
          </p>
          <CtaButton onClick={openAgent} className="px-10 py-4 rounded-sm text-base">
            <i className="ri-mic-line text-xl"></i>
            Hablar con el Agente Ahora
          </CtaButton>
        </div>
      </section>



      {/* ── FOOTER ── */}
      <footer style={{ background: '#0a1e3d', borderTop: '1px solid rgba(200,121,65,0.2)' }}>
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }} className="py-10">
          <div className="max-w-6xl mx-auto px-6 flex items-center gap-5">
            <img
              src="https://static.readdy.ai/image/257ded5cf4b1165b373a01f4236f0b3a/e052755f997086e5f1ed7f5d0f41046d.png"
              alt="Tu Revisión Segura Logo"
              className="h-14 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }}
            />
            <div className="h-10 w-px bg-white/15"></div>
            <div>
              <p className="text-white font-black text-lg tracking-widest">REVISION SEGURA</p>
              <p className="text-white/40 text-xs tracking-widest">REVISION SEGURA</p>
            </div>
          </div>
        </div>

        <div className="py-14">
          <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 gap-10">
            <div>
              <h4 className="text-white/70 text-xs font-bold uppercase tracking-widest mb-4">Sobre el Programa</h4>
              <p className="text-white/40 text-xs leading-relaxed mb-5">
                Turevisionsegura.com es una plataforma de comunicación de Gestión Automotriz AW2020 SAS. Los beneficios son otorgados por la Alianza de Beneficios y son ajenos a la tarifa legal recaudada por el Centro de Diagnóstico (CDA). Estos premios y bonos no son descuentos directos en la tarifa de la revisión.
              </p>
              <div className="flex items-center gap-2 text-white/35 text-xs">
                <i className="ri-map-pin-line text-xs" style={{ color: 'rgba(200,121,65,0.6)' }}></i>
                <span>Bogotá, Colombia</span>
              </div>
              <div className="flex items-center gap-2 text-white/35 text-xs mt-1.5">
                <i className="ri-building-line text-xs" style={{ color: 'rgba(200,121,65,0.6)' }}></i>
                <span>NIT 901401495</span>
              </div>
            </div>
            <div>
              <h4 className="text-white/70 text-xs font-bold uppercase tracking-widest mb-4">Aliados</h4>
              <ul className="flex flex-col gap-2.5">
                {['VELKO', 'CARD PLUS', 'Gestión Automotriz AW2020', 'RTM-EC'].map((name) => (
                  <li key={name} className="flex items-center gap-2 text-white/40 text-sm">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]" style={{ color: 'rgba(200,121,65,0.5)' }}></i>
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="py-5">
          <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/25 text-xs">&copy; 2025 REVISION SEGURA · Gestión Automotriz AW2020 SAS · Todos los derechos reservados.</p>
            <div className="flex items-center gap-4">
              <button onClick={() => window.open('https://lsddbkicdbwbilyhzcrf.supabase.co/storage/v1/object/public/DOCUMENTS/POLITICA-OFICIAL-PROGRAMA-CAV-O2.pdf', '_blank')}
                className="text-white/25 hover:text-white/50 text-xs transition-colors cursor-pointer whitespace-nowrap">
                Política Oficial
              </button>
              <span className="text-white/15">·</span>
              <a href="https://readdy.ai/?origin=logo" rel="nofollow" className="text-white/25 hover:text-white/50 text-xs transition-colors">Website Builder</a>
            </div>
          </div>
          <div className="max-w-6xl mx-auto px-6 mt-3">
            <p className="text-white/12 text-[10px] leading-relaxed text-center">
              Turevisionsegura.com es una plataforma de comunicación de Gestión Automotriz AW2020 SAS. Los beneficios son otorgados por la Alianza de Beneficios (Actividad Promocional) y son ajenos a la tarifa legal recaudada por el Centro de Diagnóstico (CDA). Estos premios y bonos no son descuentos directos en la tarifa de la revisión.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
